import { useCallback, useState, useMemo } from "react";
import Dropdown from "./Dropdown";
import UserAvatar from "./UserAvatar";
import { useClickOutside } from "../../../app/layouts/Header";
import { useProfile } from "../../../features/auth/hooks/useProfile";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { logoutUser } from "../../../store/thunks";
import { useNavigate } from "react-router-dom";
import useOrganization from "../../../features/organization/hooks/useOrganization";
import MobileDeptTeamSelector from "../../common/DeptTeamSelector/MobileDeptTeamSelector";
import MobileOrgSelector from "../../common/OrgSelector/MobileOrgSelector";

const UserMenu: React.FC<{ avatarClass?: string }> = ({
  avatarClass = "w-11 h-11",
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedTeamName, setSelectedTeamName] = useState<string | null>(null);

  const close = useCallback(() => setOpen(false), []);
  const ref = useClickOutside(close);
  const { user, roles, teams, teamsLeading, departmentsLeading, isCoach } =
    useProfile();
  const { currentOrganization } = useOrganization();

  // badge
  const teamBadges = useMemo(() => {
    return teams
      .filter((t, i, self) => self.findIndex((x) => x.id === t.id) === i)
      .map((t) => t.name);
  }, [teams]);

  const BADGE_COLORS = [
    "bg-[#5d4cc6] text-white",
    "bg-[#3b519c] text-white",
    "bg-[#8DE2B0] text-[#26295e]",
    "bg-[#f0f7fb] text-[#26295e]",
  ];

  const badgesToShow = selectedTeamName ? [selectedTeamName] : teamBadges;

  return (
    <div ref={ref} className="relative flex items-center">
      <button
        onClick={() => setOpen((v) => !v)}
        className="bg-transparent border-0 cursor-pointer p-0 flex"
        aria-label="User menu"
      >
        <UserAvatar src={user?.image ?? undefined} className={avatarClass} />
      </button>

      <Dropdown isOpen={open} className="w-[320px]" arrowRight={14}>
        {/* ── Close button ── */}
        <button
          onClick={close}
          className="absolute top-3 right-3 bg-transparent border-0 cursor-pointer text-[#7d86a9] text-sm leading-none hover:text-[#26295e] transition-colors z-10 w-6 h-6 flex items-center justify-center"
        >
          ✕
        </button>

        {/* ── User info — responsive flex ── */}
        <div className="flex items-start gap-3 p-4 pr-8">
          {/* Avatar */}
          <div
            className="cursor-pointer flex-shrink-0"
            onClick={() => {
              navigate("/account-settings");
              close();
            }}
          >
            <UserAvatar
              src={user?.image ?? undefined}
              className="w-14 h-14 border-[3px] border-[#7b9ded]"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p
              className="text-sm font-bold text-[#26295e] mb-0.5 underline cursor-pointer font-primary leading-tight"
              onClick={() => {
                navigate("/account-settings");
                close();
              }}
              style={{ wordBreak: "break-word" }}
            >
              {user?.first_name} {user?.last_name}
            </p>
            <p
              className="text-xs text-[#7d86a9] font-primary mb-2 leading-tight"
              style={{ wordBreak: "break-all" }}
            >
              {user?.email}
            </p>

            {/* Team badges */}
            {badgesToShow.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {badgesToShow.map((name, i) => (
                  <span
                    key={name}
                    className={`text-[10px] font-primary font-semibold px-2 py-0.5 rounded-full ${BADGE_COLORS[i % BADGE_COLORS.length]}`}
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}

            <button
              onClick={() => dispatch(logoutUser())}
              className="bg-transparent border-0 text-[#e05252] text-[11px] font-semibold cursor-pointer p-0 font-primary flex items-center gap-1 hover:text-[#c03030] transition-colors"
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Log Out
            </button>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="border-t border-[#f0f3fa]" />

        {isCoach ? (
          <>
            <MobileOrgSelector onClose={close} />
            {currentOrganization?.id && (
              <MobileDeptTeamSelector
                organizationId={currentOrganization.id}
                roles={roles}
                teams={teams}
                teamsLeading={teamsLeading}
                allowedDepartments={departmentsLeading}
                onTeamSelect={(team) => setSelectedTeamName(team.name)}
                onClose={close}
              />
            )}
          </>
        ) : (
          // ✅ Non-coach — show dept team selector only
          currentOrganization?.id && (
            <MobileDeptTeamSelector
              organizationId={currentOrganization.id}
              roles={roles}
              teams={teams}
              teamsLeading={teamsLeading}
              allowedDepartments={departmentsLeading}
              onTeamSelect={(team) => setSelectedTeamName(team.name)}
              onClose={close}
            />
          )
        )}
      </Dropdown>
    </div>
  );
};

export default UserMenu;
